import { useCallback, useState } from 'react';
import { debounce } from 'throttle-debounce';
import { useHttpApi } from './useHttpApi';
import {
  fetchValidateAddressWeb,
  IValidateAddressWebResponse200,
  IValidateAddressWebResponse400,
} from '../../../repositories/geo-suggest/v2/validate-address-web';
import { useFormTracking } from '../../../utils/useFormTracking';

export const VALIDATION_ERROR = 'Укажите дом';
export const SERVER_ERROR = 'Не удалось получить данные о доме, попробуй ввести другой адрес';

export interface IUseValidateAddress {
  onHouseIdChange(value: number | null): void;
  onLocationIdChange(value: number | null): void;
}
export const useValidateAddress = ({ onHouseIdChange, onLocationIdChange }: IUseValidateAddress) => {
  const httpApi = useHttpApi();

  const [isFetching, setIsFetching] = useState(false);
  const [validateError, setValidateError] = useState('');
  const { trackAddressFail } = useFormTracking();

  const resetValidation = useCallback(() => {
    onHouseIdChange(null);
    onLocationIdChange(null);
    setValidateError('');
  }, [onHouseIdChange, onLocationIdChange]);

  const validateAddress = useCallback(
    async (address: string) => {
      if (!httpApi) {
        return;
      }

      resetValidation();

      setIsFetching(true);

      try {
        const validateResponse = await fetchValidateAddressWeb<
          IValidateAddressWebResponse200,
          IValidateAddressWebResponse400
        >({
          httpApi,
          parameters: { address },
        });

        if (validateResponse.statusCode === 200) {
          onHouseIdChange(validateResponse.response.houseId);
          onLocationIdChange(validateResponse.response.locationId);
        } else {
          trackAddressFail();

          setValidateError(VALIDATION_ERROR);
        }
      } catch (e) {
        setValidateError(SERVER_ERROR);
      } finally {
        setIsFetching(false);
      }
    },
    [httpApi, resetValidation, onHouseIdChange, onLocationIdChange, trackAddressFail],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const validateAddressDebounced = useCallback(debounce(100, validateAddress), [validateAddress]);

  return {
    validateAddress: validateAddressDebounced,
    resetValidation,
    isFetching,
    validateError,
  };
};
